import { Button } from "@/components/ui/button";

interface ButtonData {
  text: string;
  url: string;
  variant: "primary" | "outline";
}

interface AboutSectionProps {
  title: string;
  subtitle: string;
  content: string;
  imageUrl?: string;
  buttons?: ButtonData[];
  backgroundColor?: string;
}

export function AboutSection({ title, subtitle, content, imageUrl, buttons = [], backgroundColor }: AboutSectionProps) {
  return (
    <section
      className="py-16 sm:py-24 px-4 bg-muted/30"
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className="max-w-6xl mx-auto">
        <div className={`grid gap-12 items-center ${imageUrl ? "md:grid-cols-2" : "md:grid-cols-1 text-center"}`}>
          <div className="space-y-6">
            {subtitle && (
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 px-4 py-1.5 rounded-full">
                {subtitle}
              </span>
            )}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground leading-tight tracking-tight">
              {title}
            </h2>
            <div className="text-muted-foreground text-base sm:text-lg leading-relaxed whitespace-pre-line">
              {content}
            </div>
            {buttons.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {buttons.map((btn, i) => (
                  <Button
                    key={i}
                    variant={btn.variant === "outline" ? "outline" : "default"}
                    size="lg"
                    className="font-semibold transition-all duration-300 hover:-translate-y-0.5"
                    asChild
                  >
                    <a href={btn.url} target="_blank" rel="noopener noreferrer">{btn.text}</a>
                  </Button>
                ))}
              </div>
            )}
          </div>
          {imageUrl && (
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl" />
                <img
                  src={imageUrl}
                  alt={title}
                  className="relative rounded-xl shadow-xl max-h-96 object-cover ring-1 ring-border/30"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
